// SprintEditorModal.tsx

import React, { useState } from 'react'; // --- FIX: Import useState ---
import { useTranslation } from 'react-i18next';
import { subDays, formatISO } from 'date-fns';

import { Modal, Button, TextField, TextareaField } from '@dopomogai/ui';
import { KPIObjectiveEditor } from './KPIObjectiveEditor';
import type { Sprint, Goal } from '@dopomogai/types';

interface SprintEditorModalProps {
  isOpen: boolean;
  sprintData: Sprint | null;
  /** All available goals to choose from for KPIs. */
  availableGoals: Goal[];
  isSaving: boolean;
  onClose: () => void;
  onSave: (sprint: Sprint) => void;
  onDataChange: (data: Partial<Omit<Sprint, 'id' | 'project_id'>>) => void;
}

/**
 * A global modal for creating or editing a Sprint's details, including its KPI objectives.
 */
export const SprintEditorModal: React.FC<SprintEditorModalProps> = ({
  isOpen,
  sprintData,
  availableGoals,
  isSaving,
  onClose,
  onSave,
  onDataChange,
}) => {
  const { t } = useTranslation(['admin_sprints', 'common']);

  // --- FIX: Create state to hold a reference to the modal's panel element ---
  const [modalPanelElement, setModalPanelElement] = useState<HTMLDivElement | null>(null);

  if (!sprintData) return null;

  const isNewSprint = !sprintData.id;
  const modalTitle = isNewSprint ? t('editor.newSprintTitle') : t('editor.editSprintTitle');

  const handleSave = () => {
    if (!sprintData.name.trim()) {
      alert(t('editor.nameRequiredError'));
      return;
    }
    onSave(sprintData);
  };
  
  const formatDateForInput = (dateString?: string | null) => {
      if (!dateString) return '';
      return formatISO(new Date(dateString), { representation: 'date' });
  };
  
  const handleDateChange = (field: 'start_date' | 'end_date', dateValue: string) => {
    if (!dateValue) {
        onDataChange({ [field]: null });
        return;
    }
    const date = new Date(dateValue);
    onDataChange({ [field]: date.toISOString() });
  };

  const footer = (
    <div className="flex justify-end gap-3">
      <Button textKey="common:cancel" variant="secondary" onClick={onClose} />
      <Button
        textKey={isNewSprint ? 'common:create' : 'common:saveChanges'}
        onClick={handleSave}
        loading={isSaving}
        data-modal-primary-action="true"
      />
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      footer={footer}
      size="2xl"
      // --- FIX: Use the new onPanelMount prop to capture the panel's element ref ---
      onPanelMount={setModalPanelElement}
    >
        <div className="space-y-6 p-1">
          <TextField
              label={t('editor.nameLabel')}
              value={sprintData.name}
              onChange={(e) => onDataChange({ name: e.target.value })}
              placeholder={t('editor.namePlaceholder')}
              required
          />
          <TextareaField
              label={t('editor.goalLabel')}
              value={sprintData.goal || ''}
              onChange={(e) => onDataChange({ goal: e.target.value })}
              placeholder={t('editor.goalPlaceholder')}
              rows={2}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField
                  label={t('editor.startDateLabel')}
                  type="date"
                  value={formatDateForInput(sprintData.start_date)}
                  onChange={(e) => handleDateChange('start_date', e.target.value)}
              />
              <TextField
                  label={t('editor.endDateLabel')}
                  type="date"
                  value={formatDateForInput(sprintData.end_date)}
                  onChange={(e) => handleDateChange('end_date', e.target.value)}
              />
          </div>

          <KPIObjectiveEditor
            objectives={sprintData.kpi_objectives || []}
            availableGoals={availableGoals}
            onObjectivesChange={(newObjectives) => onDataChange({ kpi_objectives: newObjectives })}
            // --- FIX: Pass the captured panel ref down to the component containing the SelectFields ---
            // Note: KPIObjectiveEditor must be updated to accept and pass this prop down to its SelectFields.
            portalContainer={modalPanelElement}
          />
        </div>
    </Modal>
  );
};